/**
 * Spec 035 v0.4, step 5: decisions for people — topics, accepting a case, «Публикуем?», a launch,
 * money. Only the heads of top units ask; they live here, not in «Решить», and go out in the
 * morning batch with buttons (urgent ones at once). One of the unit's people answers once.
 */

import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { UnitDecisions, type InstructionView, type UnitDecisionOut } from "@/api/units";
import { Button } from "@/components/ui/Button";
import { TextField } from "@/components/ui/Field";
import { useToast } from "@/components/ui/Toast";
import { Caption, Pill } from "@/kit";
import { cn } from "@/lib/cn";
import { formatRelativePast } from "@/lib/format";
import { Markdown } from "@/lib/markdown";
import { missingLine } from "@/lib/lessons";
import { decisionLine } from "@/lib/units";

/** Spec 037: «Новая инструкция» — what the new setup changes and the lessons it takes in. */
function InstructionDiff({ view }: { view: InstructionView }) {
  return (
    <div className="flex flex-col gap-2">
      {view.stale && (
        <p className="text-meta text-p-blocked">
          Карточка уже изменилась (версия {view.current_version}, предложено от {view.base_version}): принятие ничего не запишет.
        </p>
      )}
      {missingLine(view.missing) && <p className="text-meta text-p-needs">{missingLine(view.missing)}</p>}
      <ul className="max-h-80 space-y-0.5 overflow-auto rounded-card border border-p-border px-3 py-2 font-mono text-[13px] leading-5">
        {view.lines.map((line, i) => (
          <li key={i} className={cn("whitespace-pre-wrap break-words", line.sign === "+" ? "text-p-done" : "text-p-blocked line-through")}>
            {line.sign} {line.text}
          </li>
        ))}
      </ul>
      {view.lessons.length > 0 && (
        <section className="space-y-1">
          <Caption>Уроки, которые она забирает</Caption>
          <ul className="space-y-0.5 text-meta text-p-text">
            {view.lessons.map((text) => <li key={text}>· {text}</li>)}
          </ul>
        </section>
      )}
    </div>
  );
}

function DecisionCard({ decision, withUnit }: { decision: UnitDecisionOut; withUnit: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [words, setWords] = useState("");
  const [busy, setBusy] = useState(false);
  const open = decision.state === "open";

  const send = async (body: { option: number } | { answer: string }) => {
    setBusy(true);
    try {
      await UnitDecisions.answer(decision.id, body);
      toast.success("Ответ передан руководителю");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Ответ не дошёл");
    } finally {
      setBusy(false);
      void qc.invalidateQueries({ queryKey: ["departments"] });
    }
  };

  return (
    <li className="flex flex-col gap-2 rounded-card border border-p-border bg-p-surface px-4 py-3">
      <span className="text-meta text-p-text-muted">
        {decision.urgent && <span className="mr-1 font-semibold text-p-blocked">срочно ·</span>}
        №{decision.number}
        {withUnit && ` · ${decision.unit_name}`} · {decision.asked_by.name} · {formatRelativePast(new Date(decision.created_at))}
      </span>
      <span className="text-[15px] font-semibold leading-5 text-p-text">{decision.question}</span>
      {decision.context_md && <Markdown text={decision.context_md} className="text-meta text-p-text-muted" />}
      {decision.instruction && <InstructionDiff view={decision.instruction} />}
      {open ? (
        <div className="flex flex-col gap-2">
          {decision.options.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {decision.options.map((option, n) => (
                <Button key={option} size="compact" variant={n === 0 ? "strong" : "secondary"} disabled={busy}
                  onClick={() => void send({ option: n })}>
                  {option}
                </Button>
              ))}
            </div>
          )}
          <div className="flex items-end gap-2">
            <TextField label="Свой ответ" value={words} maxLength={2000} onChange={(e) => setWords(e.target.value)} />
            <Button size="compact" variant="secondary" disabled={busy || !words.trim()}
              onClick={() => void send({ answer: words.trim() })}>
              Ответить
            </Button>
          </div>
        </div>
      ) : (
        <span><Pill tone={decision.state === "answered" ? "done" : "waiting"}>{decisionLine(decision)}</Pill></span>
      )}
    </li>
  );
}

/** «Ждёт решения»: every open decision, on top of «Отделы». Nothing to show — nothing shown. */
export function OpenDecisions() {
  const open = useQuery({ queryKey: ["departments", "decisions", "open"], queryFn: UnitDecisions.open, refetchInterval: 60_000 });
  const items = open.data ?? [];
  if (!items.length) return null;
  return (
    <ul className="flex flex-col gap-2" aria-label="Ждёт решения">
      {items.map((d) => <DecisionCard key={d.id} decision={d} withUnit />)}
    </ul>
  );
}

/** A top unit's decisions, newest first. */
export function UnitDecisionsBlock({ unitId }: { unitId: string }) {
  const list = useQuery({ queryKey: ["departments", "decisions", unitId], queryFn: () => UnitDecisions.ofUnit(unitId) });
  const items = list.data ?? [];
  if (!items.length) return <p className="text-meta text-p-text-muted">Руководитель пока ничего не спрашивал.</p>;
  return <ul className="flex flex-col gap-2">{items.slice(0, 10).map((d) => <DecisionCard key={d.id} decision={d} withUnit={false} />)}</ul>;
}
